import Router from 'next/router';
import { getQueryParam, sanitiseParamValue, setQueryParam } from '../helpers/query-params';
import { PARAM_NAME_PATTERN, MUTATION_DEBOUNCE_MS, RESOLVER_PAGE } from './constants';

// The links whose href this component changed, shared by every instance: per parameter name,
// the href before the change and the href it set. A plain click on one of them is routed here,
// because Next.js Link would otherwise navigate to its original `as` url, which doesn't carry
// the parameter. Keeping the original lets a new value replace one this component added.
const updatedLinks = new WeakMap();

const isValidParamName = name => typeof name === 'string' && PARAM_NAME_PATTERN.test(name);

const isValidSelector = selector => {
  if (typeof selector !== 'string' || !selector.trim()) return false;
  try {
    document.querySelector(selector);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * The value to add: the parameter from the page url when it has a usable one, otherwise the
 * editor's default. Both are cleaned with the frontend-campaign rule. Null when neither is
 * usable.
 */
const resolveParamValue = (search, paramName, defaultValue) =>
  sanitiseParamValue(getQueryParam(`?${search.replace(/^\?/, '')}`, paramName)) ||
  sanitiseParamValue(defaultValue);

/**
 * The link's href when it points to a page on this site, otherwise null. Other domains,
 * mailto:, tel:, javascript: and fragment only links are left alone.
 */
const getSameOriginHref = (anchor, location) => {
  const href = anchor.getAttribute('href');
  if (!href || href.trim().startsWith('#')) return null;
  try {
    const url = new URL(href, location.href);
    const isWeb = url.protocol === 'http:' || url.protocol === 'https:';
    return isWeb && url.origin === location.origin ? href : null;
  } catch (error) {
    return null;
  }
};

const getRecord = (anchor, paramName) => updatedLinks.get(anchor)?.get(paramName) || null;

const setRecord = (anchor, paramName, record) => {
  const records = updatedLinks.get(anchor) || new Map();
  if (record) records.set(paramName, record);
  else records.delete(paramName);
  updatedLinks.set(anchor, records);
};

/**
 * Adds the parameter to the link's href unless the link came with one of its own. Returns
 * { href, isUpdated }: the href the link navigates to and whether this component set the
 * parameter on it. Null when the link isn't one this component handles.
 */
const updateLink = (anchor, { paramName, value }, location) => {
  const href = getSameOriginHref(anchor, location);
  if (!href) return null;
  const record = getRecord(anchor, paramName);
  // An href this component set is rebuilt from the original; any other href is the original
  const original = record && record.applied === href ? record.original : href;
  const applied = setQueryParam(original, paramName, value);
  if (applied === original) {
    setRecord(anchor, paramName, null);
    return { href: original, isUpdated: false };
  }
  if (applied !== href) anchor.setAttribute('href', applied);
  setRecord(anchor, paramName, { original, applied });
  return { href: applied, isUpdated: true };
};

const updateLinks = (options, location) => {
  document.querySelectorAll(options.selector).forEach(section => {
    section.querySelectorAll('a[href]').forEach(anchor => updateLink(anchor, options, location));
  });
};

const isPlainPrimaryClick = event =>
  event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

/**
 * Brings the href of a clicked link in the section up to date first, so a link added since the
 * last update opens with the parameter however it's opened (new tab, middle click, download).
 * Then, for a plain click on a link this component changed, returns the url to route to client
 * side. Null when the browser or Next.js should handle the click as usual: another handler
 * already took it, a new tab or window was asked for, a download, or a link that came with the
 * parameter.
 */
const getClickNavigationUrl = (event, options, location) => {
  const anchor = event.target?.closest?.('a[href]');
  if (!anchor || !anchor.closest(options.selector)) return null;
  const link = updateLink(anchor, options, location);
  // A link that came with the parameter navigates through its own Link already
  if (!link?.isUpdated || event.defaultPrevented || !isPlainPrimaryClick(event)) return null;
  const target = anchor.getAttribute('target');
  if ((target && target !== '_self') || anchor.hasAttribute('download')) return null;

  const url = new URL(link.href, location.href);
  return `${url.pathname}${url.search}${url.hash}`;
};

/**
 * Adds `paramName=<value>` to the same-origin links inside every element matching selector:
 * the value from the page url, or defaultValue when the url has no usable one. Links added
 * later are updated too, and plain clicks on updated links are routed client side to the
 * updated url. Returns a cleanup function, or null when the settings are unusable.
 */
const attachLinkQueryParam = ({ paramName, defaultValue, selector }) => {
  if (!isValidParamName(paramName) || !isValidSelector(selector)) return null;
  const { location } = window;
  const value = resolveParamValue(location.search, paramName, defaultValue);
  if (!value) return null;

  const options = { paramName, value, selector };
  updateLinks(options, location);

  let timer = null;
  const observer = new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(() => updateLinks(options, location), MUTATION_DEBOUNCE_MS);
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['href']
  });

  const onClick = event => {
    let url;
    try {
      url = getClickNavigationUrl(event, options, location);
    } catch (error) {
      return;
    }
    if (!url) return;
    // Next.js Link skips its own navigation when the click is already defaultPrevented. The
    // event keeps propagating so analytics handlers still see it.
    event.preventDefault();
    // A failed client side navigation falls back to a full one, unless a later navigation
    // cancelled it
    const navigateFully = error => {
      if (!error?.cancelled) location.assign(url);
    };
    try {
      Promise.resolve(Router.push(RESOLVER_PAGE, url)).catch(navigateFully);
    } catch (error) {
      navigateFully(error);
    }
  };
  // Middle clicks fire auxclick rather than click; the handler only updates the href for them
  document.addEventListener('click', onClick, true);
  document.addEventListener('auxclick', onClick, true);

  return () => {
    clearTimeout(timer);
    observer.disconnect();
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('auxclick', onClick, true);
  };
};

export {
  isValidParamName,
  isValidSelector,
  resolveParamValue,
  getSameOriginHref,
  updateLink,
  getClickNavigationUrl,
  attachLinkQueryParam
};
